import axios from 'axios';
import { authService } from './authService';

export class ApiService {
  constructor() {
    this.client = axios.create({
      baseURL: process.env.REACT_APP_API_URL,
      headers: { 'Content-Type': 'application/json' }
    });
    this.client.interceptors.request.use(async config => {
      if (!process.env.REACT_APP_API_URL) throw new Error('Configure REACT_APP_API_URL before using BOMWise.');
      const token = await authService.token();
      if (token) config.headers.Authorization = `Bearer ${token}`;
      else delete config.headers.Authorization;
      return config;
    });
    this.client.interceptors.response.use(response => response, async error => {
      const config = error.config;
      if (error.response?.status === 401 && config) {
        const rejectedToken = config.headers?.Authorization?.replace(/^Bearer /, '');
        if (!config._authRetried) {
          config._authRetried = true;
          const token = await authService.token({ forceRefresh: true, rejectedToken });
          if (token) {
            config.headers.Authorization = `Bearer ${token}`;
            return this.client.request(config);
          }
        }
        authService.clear(rejectedToken);
      }
      return Promise.reject(error);
    });
  }
  async getBOM(id) { return (await this.client.get(`/bom/${encodeURIComponent(id)}`)).data; }
  async getPresignedUrl({ filename, name, description = '', project_id, part_mapping }) {
    return (await this.client.post('/bom/upload/url', {
      file_extension: filename.split('.').pop().toLowerCase(),
      bom_name: name ?? filename.replace(/\.[^/.]+$/, ''),
      description, ...(project_id ? { project_id } : {}),
      ...(part_mapping ? { part_mapping } : {})
    })).data;
  }
  async uploadToS3(upload, file) {
    const form = new FormData();
    Object.entries(upload.fields).forEach(([key, value]) => form.append(key, value));
    form.append('file', file);
    // Do not send the API Authorization header to S3.
    return axios.post(upload.url, form);
  }
  async mutateBOM(bomId, revision, operation, data = {}) {
    return (await this.client.post('/bom/items/update', { ...data, bom_id: bomId, revision, operation })).data;
  }
  async updateBOMMetadata(bomId, revision, data) {
    return (await this.client.post('/bom/metadata', { ...data, bom_id: bomId, revision })).data;
  }
  async sharing(bomId, operation, data = {}) {
    return (await this.client.post('/bom/permissions', { ...data, bom_id: bomId, operation })).data;
  }
  async exportBOM(data) { return (await this.client.post('/bom/export', data)).data; }
  async listProjects() { return (await this.client.get('/projects')).data; }
  async listWorkspaces() { return (await this.client.get('/workspaces')).data; }
  async getWorkspace(id) { return (await this.client.get(`/workspaces/${encodeURIComponent(id)}`)).data; }
  async mutateWorkspace(operation, data) {
    return (await this.client.post('/workspaces/mutate', { ...data, operation })).data;
  }
  async exportWorkspace(data) { return (await this.client.post('/workspaces/export', data)).data; }
  async listQuotes(bomId, lineId, cursor) {
    return (await this.client.get(`/bom/${encodeURIComponent(bomId)}/quotes`, {
      params: { line_item_id: lineId, ...(cursor ? { cursor } : {}) }
    })).data;
  }
  async mutateQuote(bomId, lineId, revision, operation, data) {
    return (await this.client.post('/bom/quotes', {
      ...data, bom_id: bomId, line_item_id: lineId, revision, operation
    })).data;
  }
  async searchParts(data) { return (await this.client.post('/parts/search', data)).data; }
  // Retired analysis routes must not silently fall back to public result objects.
  async getProject() { throw new Error('Legacy analysis is retired. Reimport this BOM to use the secure editor.'); }
  async processBOM() { throw new Error('Use the authenticated upload screen.'); }
}

export const apiService = new ApiService();
